import React from 'react';

interface TableSkeletonProps {
    /** Number of placeholder rows to render. */
    rows?: number;
    /** Number of columns per row. */
    columns?: number;
}

const COLUMN_WIDTHS = ['70%', '52%', '64%', '46%', '60%', '50%', '42%'];

/**
 * Loading placeholder that mirrors the table row layout (56px rows, 16px
 * padding, evenly-split columns) so a list keeps its shape while data loads
 * instead of blanking the area with a full-screen spinner.
 *
 * Shimmer is a self-contained inline <style> (the repo's existing pattern,
 * e.g. ReportPageShell) so it needs no CSS-import/postcss and never depends
 * on the host app's Tailwind. Respects prefers-reduced-motion.
 */
const TableSkeleton: React.FC<TableSkeletonProps> = ({ rows = 8, columns = 5 }) => {
    const cols = Math.max(1, columns);
    const Bar: React.FC<{ width: string }> = ({ width }) => (
        <div className="inv-skeleton" style={{ width, height: 12, borderRadius: 6 }} />
    );
    return (
        <div style={{ width: '100%' }} aria-busy="true" role="status">
            <style>{`
                .inv-skeleton { position: relative; overflow: hidden; background-color: #E9EFEF; border-radius: 6px; }
                .inv-skeleton::after {
                    content: ""; position: absolute; inset: 0; transform: translateX(-100%);
                    background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0) 100%);
                    animation: inv-skeleton-shimmer 1.4s ease-in-out infinite;
                }
                @keyframes inv-skeleton-shimmer { 100% { transform: translateX(100%); } }
                @media (prefers-reduced-motion: reduce) { .inv-skeleton::after { animation: none; } }
            `}</style>
            {Array.from({ length: rows }).map((_, r) => (
                <div
                    key={r}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        width: '100%',
                        height: '56px',
                        padding: '10px 16px',
                        borderBottom: r === rows - 1 ? 'none' : '1px solid #DBE4E4',
                    }}
                >
                    {Array.from({ length: cols }).map((_, c) => (
                        <div key={c} style={{ flex: 1 }}>
                            <Bar width={COLUMN_WIDTHS[(r + c) % COLUMN_WIDTHS.length]} />
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

export default TableSkeleton;
